"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { 
  PenSquare,
  Star,
  StarHalf,
  MessageSquare,
  ThumbsUp,
  Plus,
  Minus,
  CheckCircle,
  XCircle,
  LogIn
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getCarReviews, submitCarReview, hasUserReviewedCar, getCurrentUser, markReviewAsHelpful } from "@/actions/client-reviews";

interface ReviewSectionProps {
  carId: string;
}

// Define a type for the user object
interface User {
  id: string;
  name?: string;
  email?: string;
  image?: string | null;  // Allow null value for image
}

// Define review form type
interface ReviewFormData {
  title: string;
  content: string;
  rating: number;
  pros: string[];
  cons: string[];
}

export function ReviewSection({ carId }: ReviewSectionProps) {
  const [reviewData, setReviewData] = useState<{
    averageRating: number;
    totalReviews: number;
    ratingDistribution: Record<number, number>;
    recommendationPercentage: number;
    recentReviews: Array<{
      id: string;
      title: string;
      content: string;
      rating: number;
      userName: string;
      userAvatar: string | null;
      createdAt: Date;
      pros: string[] | null;
      cons: string[] | null;
      helpfulCount?: number;
      unhelpfulCount?: number;
    }>;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isLoginDialogOpen, setIsLoginDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [reviewForm, setReviewForm] = useState<ReviewFormData>({
    title: "",
    content: "",
    rating: 5,
    pros: [""],
    cons: [""]
  });
  const [reviewHelpfulness, setReviewHelpfulness] = useState<Record<string, boolean | null>>({});
  const [isVoting, setIsVoting] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Fetch user info
        const userResult = await getCurrentUser();
        if (userResult.success) {
          setCurrentUser(userResult.user);
        }
        
        // Fetch review status
        const reviewedResult = await hasUserReviewedCar(carId);
        if (reviewedResult.success) {
          setHasReviewed(reviewedResult.hasReviewed);
        }
        
        // Fetch reviews
        const result = await getCarReviews(carId);
        if (result.success && result.data) {
          setReviewData(result.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [carId]);

  // Handler for review form changes
  const handleReviewChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setReviewForm(prev => ({ ...prev, [name]: value }));
  };

  // Handler for rating selection
  const handleRatingChange = (newRating: number) => {
    setReviewForm({ ...reviewForm, rating: newRating });
  };

  // Handlers for pros and cons
  const handleProsChange = (index: number, value: string) => {
    setReviewForm(prev => {
      const newPros = [...prev.pros];
      newPros[index] = value;
      return { ...prev, pros: newPros };
    });
  };

  const handleConsChange = (index: number, value: string) => {
    setReviewForm(prev => {
      const newCons = [...prev.cons];
      newCons[index] = value;
      return { ...prev, cons: newCons };
    });
  };

  const addProsCons = (type: 'pros' | 'cons') => {
    setReviewForm(prev => {
      const newItems = [...prev[type], ""];
      return { ...prev, [type]: newItems };
    });
  };

  const removeProsCons = (type: 'pros' | 'cons', index: number) => {
    setReviewForm(prev => {
      const newItems = prev[type].filter((_, i) => i !== index);
      // Always keep at least one empty item
      if (newItems.length === 0) newItems.push("");
      return { ...prev, [type]: newItems };
    });
  };

  // Handle write review button click - check auth first
  const handleWriteReviewClick = async () => {
    if (!currentUser) {
      setIsLoginDialogOpen(true);
      return;
    }
    
    if (hasReviewed) {
      toast.info("You've already submitted a review for this car", {
        description: "You can only submit one review per car"
      });
      return;
    }
    
    setIsReviewDialogOpen(true);
  };

  // Submit review
  const submitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Filter out empty pros and cons
      const filteredPros = reviewForm.pros.filter(pro => pro.trim() !== "");
      const filteredCons = reviewForm.cons.filter(con => con.trim() !== "");
      
      // Submit review using our server action
      const result = await submitCarReview(carId, {
        title: reviewForm.title,
        content: reviewForm.content,
        rating: reviewForm.rating,
        pros: filteredPros,
        cons: filteredCons
      });
      
      if (result.success) {
        toast.success("Review submitted successfully", {
          description: "Thank you for sharing your experience! Your review will be published after moderation."
        });
        
        // Set has reviewed to true
        setHasReviewed(true);
        
        // Reset form and close dialog
        setReviewForm({
          title: "",
          content: "",
          rating: 5,
          pros: [""],
          cons: [""]
        });
        setIsReviewDialogOpen(false);
      } else {
        toast.error("Failed to submit review", {
          description: result.error || "Please try again later."
        });
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review", {
        description: "Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add a new function to handle marking reviews as helpful or unhelpful
  const handleHelpfulnessVote = async (reviewId: string, isHelpful: boolean) => {
    // If not logged in, show login dialog
    if (!currentUser) {
      setIsLoginDialogOpen(true);
      return;
    }
    
    // Prevent double-clicks
    if (isVoting[reviewId]) return;
    
    try {
      setIsVoting(prev => ({ ...prev, [reviewId]: true }));
      
      const result = await markReviewAsHelpful(reviewId, isHelpful);
      
      if (result.success) {
        // If the vote was removed, set the helpfulness to null
        if (result.removed) {
          setReviewHelpfulness(prev => ({ ...prev, [reviewId]: null }));
        } else {
          // Otherwise, set it to the new value
          setReviewHelpfulness(prev => ({ ...prev, [reviewId]: isHelpful }));
        }
        
        // Refresh the reviews to get updated counts
        const refreshResult = await getCarReviews(carId);
        if (refreshResult.success && refreshResult.data) {
          setReviewData(refreshResult.data);
        }
      } else {
        toast.error("Failed to record your vote", {
          description: result.error || "Please try again later."
        });
      }
    } catch (error) {
      console.error("Error voting for review:", error);
    } finally {
      setIsVoting(prev => ({ ...prev, [reviewId]: false }));
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  // Helper function to render stars
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="h-4 w-4 fill-primary text-primary" />);
    }

    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="h-4 w-4 fill-primary text-primary" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-muted-foreground" />);
    }

    return stars;
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="relative rounded-xl overflow-hidden glossy-card p-6 space-y-6">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-2">
              <div className="h-7 w-48 bg-primary/10 rounded-md animate-pulse"></div>
              <div className="h-4 w-32 bg-muted/20 rounded-md animate-pulse"></div>
            </div>
            <div className="h-10 w-32 bg-primary/10 rounded-full animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="h-24 bg-muted/10 rounded-lg animate-pulse"></div>
              <div className="h-24 bg-muted/10 rounded-lg animate-pulse"></div>
            </div>
            <div className="h-64 bg-muted/10 rounded-lg animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="relative rounded-xl overflow-hidden glossy-card"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute inset-0 rounded-xl border border-primary/10 neon-glow"></div>
      
      <div className="p-6 relative z-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Customer Reviews
            </h2>
            <p className="text-muted-foreground">{reviewData?.totalReviews || 0} reviews from verified buyers</p>
          </div>
          
          {/* Write Review Button */}
          <Button 
            className="rounded-full gap-2 hover-glow"
            onClick={handleWriteReviewClick}
          >
            <PenSquare className="h-4 w-4" />
            Write a Review
          </Button>
          
          {/* Review Dialog */}
          <Dialog open={isReviewDialogOpen} onOpenChange={setIsReviewDialogOpen}>
            <DialogContent className="max-w-xl border-primary/20 neon-glow bg-background/95 backdrop-blur-sm">
              <div className="absolute inset-0 bg-grid-pattern opacity-5 rounded-lg"></div>
              <DialogHeader className="relative z-10">
                <DialogTitle className="text-2xl text-center bg-gradient-to-r from-primary to-indigo-500 bg-clip-text text-transparent font-semibold">
                  Share Your Experience
                </DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  Let others know what you think about this vehicle
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={submitReview} className="space-y-5 mt-2 relative z-10">
                {/* Rating selector */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium">Your Rating</label>
                  <div className="flex justify-center gap-2 py-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleRatingChange(star)}
                        className="focus:outline-none transition-transform hover:scale-110"
                      >
                        <Star 
                          className={`h-8 w-8 ${
                            star <= reviewForm.rating
                              ? "fill-yellow-500 text-yellow-500"
                              : "text-muted-foreground"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Title */}
                <div className="space-y-2">
                  <label htmlFor="title" className="block text-sm font-medium">Review Title</label>
                  <Input
                    id="title"
                    name="title"
                    placeholder="Summarize your experience"
                    value={reviewForm.title}
                    onChange={handleReviewChange}
                    required
                    className="border-primary/20 focus:border-primary/40 focus:neon-glow bg-background/50"
                  />
                </div>
                
                {/* Content */}
                <div className="space-y-2">
                  <label htmlFor="content" className="block text-sm font-medium">Review Details</label>
                  <Textarea
                    id="content"
                    name="content"
                    placeholder="Share your experience with this vehicle"
                    value={reviewForm.content}
                    onChange={handleReviewChange}
                    required
                    className="min-h-[100px] border-primary/20 focus:border-primary/40 focus:neon-glow bg-background/50"
                  />
                </div>
                
                {/* Pros */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">What did you like?</label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="h-8 gap-1 border-green-500/30 text-green-500 hover:bg-green-500/10"
                      onClick={() => addProsCons('pros')}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Add</span>
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {reviewForm.pros.map((pro, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <CheckCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                          <Input
                            placeholder="What did you like about this vehicle?"
                            value={pro}
                            onChange={(e) => handleProsChange(index, e.target.value)}
                            className="pl-10 border-green-500/20 focus:border-green-500/40 focus:neon-glow-green bg-background/50"
                          />
                        </div>
                        {reviewForm.pros.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500"
                            onClick={() => removeProsCons('pros', index)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Cons */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">What could be improved?</label>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="h-8 gap-1 border-red-500/30 text-red-500 hover:bg-red-500/10"
                      onClick={() => addProsCons('cons')}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Add</span>
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {reviewForm.cons.map((con, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <XCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                          <Input
                            placeholder="What could be improved?"
                            value={con}
                            onChange={(e) => handleConsChange(index, e.target.value)}
                            className="pl-10 border-red-500/20 focus:border-red-500/40 focus:neon-glow-red bg-background/50"
                          />
                        </div>
                        {reviewForm.cons.length > 1 && (
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500"
                            onClick={() => removeProsCons('cons', index)}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto rounded-full shadow-[0_0_15px_rgba(0,225,225,0.3)] hover:shadow-[0_0_20px_rgba(0,225,225,0.5)]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Review"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Login Dialog */}
          <Dialog open={isLoginDialogOpen} onOpenChange={setIsLoginDialogOpen}>
            <DialogContent className="max-w-md border-primary/20 neon-glow bg-background/95 backdrop-blur-sm">
              <div className="absolute inset-0 bg-grid-pattern opacity-5 rounded-lg z-0"></div>
              <DialogHeader className="relative z-10">
                <DialogTitle className="text-xl text-center">Sign in Required</DialogTitle>
                <DialogDescription className="text-center text-muted-foreground">
                  You need to sign in to submit a review
                </DialogDescription>
              </DialogHeader>
              
              <div className="flex flex-col items-center justify-center py-6 space-y-4 relative z-10">
                <div className="bg-primary/10 rounded-full p-6">
                  <LogIn className="h-10 w-10 text-primary" />
                </div>
                <p className="text-center">Please sign in to share your experience with this vehicle. Your review helps other customers make informed decisions.</p>
                
                <div className="flex gap-4 mt-4">
                  <Button variant="outline" onClick={() => setIsLoginDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button asChild>
                    <Link href={`/sign-in?callbackUrl=${encodeURIComponent(window.location.pathname)}`}>
                      Sign In
                    </Link>
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        
        {reviewData && reviewData.totalReviews > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Rating Summary */}
            <div className="md:col-span-1 space-y-6">
              <div className="flex flex-col items-center text-center bg-muted/5 p-6 rounded-xl border border-primary/10">
                <div className="text-5xl font-bold neon-text">{reviewData.averageRating.toFixed(1)}</div>
                <div className="flex my-2">{renderStars(reviewData.averageRating)}</div>
                <p className="text-sm text-muted-foreground">{reviewData.totalReviews} total reviews</p>
              </div>
              
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = reviewData.ratingDistribution[rating] || 0;
                  const percentage = reviewData.totalReviews > 0
                    ? Math.round((count / reviewData.totalReviews) * 100)
                    : 0;
                  
                  return (
                    <div key={rating} className="flex items-center gap-2">
                      <div className="flex items-center gap-1 w-14">
                        <Star className="h-4 w-4 fill-primary text-primary" />
                        <span className="text-sm">{rating}</span>
                      </div>
                      <Progress value={percentage} className="h-2 flex-1" />
                      <span className="text-sm text-muted-foreground w-9 text-right">{percentage}%</span>
                    </div>
                  );
                })}
              </div>
              
              <div className="bg-muted/5 p-4 rounded-xl border border-primary/10 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <ThumbsUp className="h-5 w-5 text-primary" />
                  <span className="text-xl font-bold">{reviewData.recommendationPercentage}%</span>
                </div>
                <p className="text-sm text-muted-foreground">of customers recommend this vehicle</p>
              </div>
            </div>
            
            {/* Recent Reviews */}
            <div className="md:col-span-2 space-y-6">
              {reviewData.recentReviews.length > 0 ? (
                reviewData.recentReviews.map((review) => (
                  <motion.div
                    key={review.id}
                    variants={itemVariants}
                    className="relative rounded-xl overflow-hidden bg-muted/5 border border-primary/10 p-6"
                  >
                    <div className="absolute top-0 right-0 bg-primary/10 px-3 py-1 text-xs font-medium rounded-bl-lg">
                      {formatDate(review.createdAt)}
                    </div>
                    
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-10 w-10 rounded-full bg-muted/30 flex items-center justify-center shrink-0 border border-primary/10">
                        {review.userAvatar ? (
                          <img 
                            src={review.userAvatar} 
                            alt={review.userName} 
                            className="h-full w-full rounded-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-medium">{review.userName.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                      
                      <div>
                        <p className="font-medium">{review.userName}</p>
                        <div className="flex mt-0.5">{renderStars(review.rating)}</div>
                      </div>
                    </div>
                    
                    <h4 className="text-lg font-semibold mb-2">{review.title}</h4>
                    <p className="text-sm text-muted-foreground mb-4">{review.content}</p>
                    
                    {(review.pros && review.pros.length > 0) || (review.cons && review.cons.length > 0) ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        {review.pros && review.pros.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-green-500">Pros</h5>
                            <ul className="text-sm space-y-1">
                              {review.pros.map((pro, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <ThumbsUp className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                  <span>{pro}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {review.cons && review.cons.length > 0 && (
                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-red-500">Cons</h5>
                            <ul className="text-sm space-y-1">
                              {review.cons.map((con, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <ThumbsUp className="h-4 w-4 text-red-500 shrink-0 mt-0.5 rotate-180" />
                                  <span>{con}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : null}
                    
                    {/* Add helpfulness controls */}
                    <div className="flex items-center justify-end gap-4 mt-4 pt-3 border-t border-primary/10">
                      <div className="text-sm text-muted-foreground">Was this review helpful?</div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`rounded-full h-8 ${reviewHelpfulness[review.id] === true ? 'bg-green-500/20 text-green-500' : ''}`}
                          disabled={isVoting[review.id]}
                          onClick={() => handleHelpfulnessVote(review.id, true)}
                        >
                          <ThumbsUp className={`h-4 w-4 mr-1 ${reviewHelpfulness[review.id] === true ? 'fill-green-500' : ''}`} />
                          <span>{review.helpfulCount || 0}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`rounded-full h-8 ${reviewHelpfulness[review.id] === false ? 'bg-red-500/20 text-red-500' : ''}`}
                          disabled={isVoting[review.id]}
                          onClick={() => handleHelpfulnessVote(review.id, false)}
                        >
                          <ThumbsUp className={`h-4 w-4 mr-1 rotate-180 ${reviewHelpfulness[review.id] === false ? 'fill-red-500' : ''}`} />
                          <span>{review.unhelpfulCount || 0}</span>
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/30" />
                  <h3 className="mt-4 text-xl font-semibold">No reviews yet</h3>
                  <p className="mt-2 text-muted-foreground">Be the first to share your experience with this vehicle</p>
                  <Button 
                    className="mt-6 rounded-full gap-2 hover-glow"
                    onClick={handleWriteReviewClick}
                  >
                    <PenSquare className="h-4 w-4" />
                    Write a Review
                  </Button>
                </div>
              )}
              
              {reviewData.totalReviews > reviewData.recentReviews.length && (
                <div className="text-center mt-6">
                  <Link href={`/cars/reviews/${carId}`}>
                    <Button variant="outline" className="rounded-full hover-glow">
                      View All Reviews
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <MessageSquare className="h-16 w-16 mx-auto text-muted-foreground/30" />
            <h3 className="mt-6 text-2xl font-semibold">No reviews yet</h3>
            <p className="mt-2 text-muted-foreground">Be the first to share your experience with this vehicle</p>
            <Button 
              className="mt-8 rounded-full gap-2 hover-glow"
              onClick={handleWriteReviewClick}
            >
              <PenSquare className="h-4 w-4" />
              Write a Review
            </Button>
          </div>
        )}
      </div>
    </motion.div>
  );
} 